import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SEOHead } from "@/components/SEOHead";
import { CheckCircle2, Clock, XCircle } from "lucide-react";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const sessionId = params.get("session_id");
  const razorpayPaymentId = params.get("razorpay_payment_id");
  const [order, setOrder] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(
    razorpayPaymentId ? "paid" : (sessionId ? "checking" : "cod")
  );
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (orderId) {
      api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => {});
    }
  }, [orderId]);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= 8) {
        setPaymentStatus("timeout");
        return;
      }
      attemptsRef.current += 1;
      try {
        const r = await api.get(`/payments/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setPaymentStatus("paid");
          if (orderId) {
            const o = await api.get(`/orders/${orderId}`);
            setOrder(o.data);
          }
          return;
        }
        if (r.data.status === "expired") {
          setPaymentStatus("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, orderId]);

  return (
    <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="order-success-page">
      <SEOHead title="Order Confirmed | Cocoamaya Patisserie" noindex />
      {paymentStatus === "checking" && (
        <>
          <Clock size={48} strokeWidth={1.5} className="mx-auto mb-6" style={{ color: "var(--accent)" }} />
          <h1 className="font-display text-4xl mb-4">Confirming your payment…</h1>
          <p style={{ color: "var(--ink-soft)" }}>This usually takes a few seconds.</p>
        </>
      )}
      {(paymentStatus === "paid" || paymentStatus === "cod") && (
        <>
          <CheckCircle2 size={48} strokeWidth={1.5} className="mx-auto mb-6" style={{ color: "var(--success)" }} />
          <h1 className="font-display text-5xl mb-4">Thank you, your order is in.</h1>
          <p className="text-lg" style={{ color: "var(--ink-soft)" }}>
            We've sent a confirmation to <strong>{order?.customer_email}</strong>. We'll be in touch.
          </p>
          {order && (
            <div className="mt-10 text-left p-8 border inline-block min-w-[320px]" style={{ borderColor: "var(--line)" }} data-testid="order-summary">
              <p className="eyebrow mb-3">Order reference</p>
              <p className="font-mono text-sm break-all">{order.id}</p>
              <p className="eyebrow mt-5 mb-2">Total</p>
              <p className="font-display text-2xl">₹{Math.round(order.total)}</p>
              <p className="eyebrow mt-5 mb-2">Status</p>
              <p className="capitalize">{order.status}</p>
            </div>
          )}
          <div className="mt-10 flex gap-4 justify-center">
            <Link to={`/track-order?id=${orderId}&email=${order?.customer_email || ""}`} className="btn-primary" data-testid="track-this-order">
              Track this order
            </Link>
            <Link to="/shop" className="btn-secondary">Back to shop</Link>
          </div>
        </>
      )}
      {(paymentStatus === "timeout" || paymentStatus === "expired") && (
        <>
          <XCircle size={48} strokeWidth={1.5} className="mx-auto mb-6" style={{ color: "var(--accent)" }} />
          <h1 className="font-display text-4xl mb-4">We couldn't confirm your payment.</h1>
          <p style={{ color: "var(--ink-soft)" }}>If you were charged, please email us with your order ID.</p>
          <Link to="/shop" className="btn-primary mt-8 inline-flex">Back to shop</Link>
        </>
      )}
    </div>
  );
}
